import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AutoComplete,
  Button,
  message,
  Modal,
  Select,
  Steps,
  Upload,
} from "antd";
import { InboxOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { FaLocationArrow, FaSearchLocation } from "react-icons/fa";
import { GrLinkPrevious, GrLinkNext } from "react-icons/gr";
import { LiaDrawPolygonSolid } from "react-icons/lia";
import {
  MdOutlineMyLocation,
  MdOutlineNextPlan,
  MdOutlineSatellite,
  MdOutlineUploadFile,
  MdPinDrop,
  MdRebaseEdit,
} from "react-icons/md";
import secureLocalStorage from "react-secure-storage";
import { motion } from "framer-motion";
import axios from "axios";
import shp from "shpjs";
import hostURL from "../../../Common/constants";
import { ModuleContext } from "../../../context/Context";
import NonGeoCheck from "../Components/NonGeoCheck";
import drawPolygonVideo from "../../../videos/draw_polygon.mp4";
import uploadShapefileVideo from "../../../videos/upload_shapefile.mp4";
import dropPinVideo from "../../../videos/drop_pin.mp4";
import pickFieldVideo from "../../../videos/pick_field.mp4";
import "../style.css";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedCounty,
  setSelectedFips,
  setSelectedState,
} from "../../../Redux/filterSlice";
import {
  fetchGeoCountiesByState,
  fetchGeoStates,
} from "../../../Redux/geo/geoThunk";
import { fetchAllPracticeDataByCounty } from "../../../Redux/practice/practiceThunk";
import {
  nextStep,
  setCurrentStep,
  setDirection,
  setIsLoading,
} from "../../../Redux/tutorialSlice";
import useTutorialSteps from "../../Tutorial/useTutorialSteps";

const Sidebar = ({
  mapZoomLevel,
  mapViewBounds,
  setForceZoomToCounty,
  userLocation,
  setUserLocation,
  pinCenter,
  setPinCenter,
  setFieldBoundary,
  selectedField,
  setSelectedField,
  setDrawingMode,
  editingMode,
  setEditingMode,
  setPinningMode,
  setLoading,
  setDrawerOpen,
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [addressValue, setAddressValue] = useState("");
  const [addressOptions, setAddressOptions] = useState([]);
  const [addressObjects, setAddressObjects] = useState([]);
  const [inputMode, setInputMode] = useState(null);
  const [showDrawPolygonVideo, setShowDrawPolygonVideo] = useState(false);
  const [showUploadShapefileVideo, setShowUploadShapefileVideo] =
    useState(false);
  const [showDropPinVideo, setShowDropPinVideo] = useState(false);
  const [showPickFieldVideo, setShowPickFieldVideo] = useState(false);
  const [showPolygonAlert, setShowPolygonAlert] = useState(false);
  const [showShapefileAlert, setShowShapefileAlert] = useState(false);
  const [showPinAlert, setShowPinAlert] = useState(false);
  const [showFieldAlert, setShowFieldAlert] = useState(false);
  const [showNonGeoCheck, setShowNonGeoCheck] = useState(false);
  const [hasFetchedPractices, setHasFetchedPractices] = useState(false);
  const [polygonAlertMessage, setPolygonAlertMessage] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  const { featureList, updateFieldDetails, practicePreference } =
    useContext(ModuleContext);
  const { Dragger } = Upload;
  const { selectedState, selectedCounty, selectedFips } = useSelector(
    (state) => state.filter
  );
  const { stateList, countyList, countyListData } = useSelector(
    (state) => state.geo
  );
  const { isTutorialActive, currentStep, direction } = useSelector(
    (state) => state.tutorial
  );
  const { handleNextStep } = useTutorialSteps();
  const prevTutorialStep = useRef(currentStep);

  const DRAW_START_STEP = 47; // first “drawing” step (Zoom In)
  const bounceBackToStart = () => {
    if (isTutorialActive) {
      dispatch(setIsLoading(false)); // stop tutorial spinner
      dispatch(setDirection("prev")); // trigger Map.jsx reset effect
      dispatch(setCurrentStep(DRAW_START_STEP));
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1000);
    handleResize(); // initial
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && inputMode === "field" && selectedField) {
      if (typeof setDrawerOpen === "function") setDrawerOpen(true);
    }
  }, [isMobile, inputMode, selectedField, setDrawerOpen]);

  const notifyAndBounce = (msg) => {
    // keep your sidebar alert too, so it’s visible after tutorial overlay
    setPolygonAlertMessage(msg);
    setShowPolygonAlert(true);
    setDrawerOpen(true);
    // stop any spinners first (important if you set them before submit)
    dispatch(setIsLoading(false));
    setLoading(false);

    // native alert is always topmost and blocks until OK tapped
    window.alert(`Drawing issue\n\n${msg}`);

    // after user dismisses, *then* bounce back to the first drawing step
    bounceBackToStart();
  };

  useEffect(() => {
    if (!isTutorialActive) return;

    // Read the previous step BEFORE updating it
    const prev = prevTutorialStep.current;

    // Only when actually stepping backward 36 -> 35
    if (prev === 36 && currentStep === 35 && prev > currentStep) {
      backToStep1();
    }

    if (currentStep === 48 || currentStep === 46) {
      setInputMode("draw");
      setDrawingMode(true);
      setShowNonGeoCheck(false);
    } else if (currentStep === 47) {
      setInputMode(null);
      setDrawingMode(false);
      setShowNonGeoCheck(false);
    } else if (currentStep === 40) {
      setInputMode("draw");
      setDrawingMode(true);
      setShowNonGeoCheck(true);
    }

    // Update the "previous" tracker AFTER handling the transition
    prevTutorialStep.current = currentStep;
  }, [currentStep, isTutorialActive]);

  const dispatch = useDispatch();

  useEffect(() => {
    const isValidFips = /^\d{5}$/.test(selectedFips);
    if (selectedFips && isValidFips && !hasFetchedPractices) {
      dispatch(fetchAllPracticeDataByCounty(selectedFips));
      setHasFetchedPractices(true);
    }
  }, [selectedFips, dispatch, hasFetchedPractices]);

  const variants = {
    open: {
      y: 0,
      opacity: 1,
      transition: {
        y: { stiffness: 1000, velocity: -100 },
      },
    },
    closed: {
      y: 50,
      opacity: 0,
      transition: {
        y: { stiffness: 1000 },
      },
    },
  };

  const hoverEffects = {
    opacity: 1,
    backgroundColor: "rgba(128, 0, 0, 0.4)",
  };

  const authToken = secureLocalStorage.getItem("token");

  const backToPracticeList = () => {
    secureLocalStorage.setItem("IsPastSearch", true);
    navigate("/PracticeList/pastSearch");
  };

  const backToStep0 = () => {
    setForceZoomToCounty(false);
    setStep(0);
  };

  const backToStep1 = () => {
    setInputMode(null);
    setPinCenter(null);
    setFieldBoundary(null);
    setSelectedField(null);
    setDrawingMode(false);
    setPinningMode(false);
    setEditingMode(false);
  };

  const onStateChange = (newSelectedState) => {
    if (!newSelectedState) {
      dispatch(setSelectedState(null));
      dispatch(setSelectedCounty(null));
      dispatch(setSelectedFips(null));
      return;
    }

    dispatch(setSelectedState(newSelectedState));
    dispatch(setSelectedCounty(null));
    dispatch(setSelectedFips(null));
    dispatch(fetchGeoCountiesByState(newSelectedState));
  };

  const onCountyChange = (countyName) => {
    if (!countyName) {
      dispatch(setSelectedCounty(null));
      dispatch(setSelectedFips(null));
      return;
    }

    dispatch(setSelectedCounty(countyName));

    const match = countyListData.find(
      (item) => item.cname?.toLowerCase() === countyName.toLowerCase()
    );

    if (match?.fips) {
      const padded = String(match.fips).padStart(5, "0");
      dispatch(setSelectedFips(padded));
      setForceZoomToCounty(true);
    } else {
      dispatch(setSelectedFips(null));
    }
  };

  const onNextClick = () => {
    if (selectedState && selectedCounty && selectedFips) {
      setStep(1);
    }
  };

  const getLocationDetails = async (longitude, latitude) => {
    const data = {
      type: "Point",
      coordinates: [longitude, latitude],
    };
    const response = await axios.post(hostURL + "api/field/details", { data });
    if (response.data) {
      setUserLocation({
        ...userLocation,
        state: response.data.countyRate.rows[0].state,
        county: response.data.countyRate.rows[0].county,
      });
    }
  };

  const locationOptions = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
  };

  const onLocationSuccess = (location) => {
    var coordinates = location.coords;
    setUserLocation({
      longitude: coordinates.longitude,
      latitude: coordinates.latitude,
    });
    getLocationDetails(coordinates.longitude, coordinates.latitude);
    setStep(1);
  };

  const onLocationError = (error) => {
    console.warn(`ERROR(${error.code}): ${error.message}`);
  };

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then(function (result) {
          if (result.state === "granted") {
            navigator.geolocation.getCurrentPosition(
              onLocationSuccess,
              onLocationError,
              locationOptions
            );
          } else if (result.state === "prompt") {
            navigator.geolocation.getCurrentPosition(
              onLocationSuccess,
              onLocationError,
              locationOptions
            );
          } else if (result.state === "denied") {
            console.log(result);
          }
        });
    } else {
      console.log("Geolocation is not supported by this browser.");
    }
  };

  const getAddressOptions = async (address) => {
    const response = await axios.get(
      "https://api.mapbox.com/geocoding/v5/mapbox.places/" +
        address +
        ".json?proximity=ip&access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
    );
    if (response.data) {
      setAddressObjects(response.data);
      const options = response.data.features.map((feature) => {
        return { key: feature.id, value: feature.place_name };
      });
      return options;
    }
  };

  const onAddressChange = async (value) => {
    setAddressValue(value);
    if (value) {
      const options = await getAddressOptions(value);
      setAddressOptions(options);
    }
  };

  const onAddressSubmit = () => {
    const addressFeature = addressObjects.features.filter(
      (feature) => feature.place_name === addressValue
    )[0];
    setUserLocation({
      longitude: addressFeature.center[0],
      latitude: addressFeature.center[1],
    });
    getLocationDetails(addressFeature.center[0], addressFeature.center[1]);
    setStep(1);
  };

  const loadPinnedField = async () => {
    const data = {
      type: "Point",
      coordinates: [pinCenter.lng, pinCenter.lat],
    };

    try {
      const response = await axios.post(hostURL + "api/field", { data });
      const fieldBoundaryFeatures = response.data.field.rows.map((feature) => {
        const geometry = JSON.parse(feature.geometry);
        return {
          type: "Feature",
          geometry: geometry,
          id: feature.gid,
          properties: { gid: feature.gid },
        };
      });
      if (fieldBoundaryFeatures.length === 0) {
        setShowPinAlert(true);
        setDrawerOpen(true);
      } else {
        const fieldBoundaryData = fieldBoundaryFeatures[0];
        setFieldBoundary(fieldBoundaryData);
        setSelectedField(fieldBoundaryData);

        // Re-open the drawer on mobile so the user can proceed
        if (typeof setDrawerOpen === "function") setDrawerOpen(true);
      }
    } catch (error) {
      console.log(error.response.data);
    }
  };

  const loadFieldBoundary = async () => {
    const data = {
      type: "Polygon",
      coordinates: [
        [
          [mapViewBounds._ne.lng, mapViewBounds._ne.lat],
          [mapViewBounds._ne.lng, mapViewBounds._sw.lat],
          [mapViewBounds._sw.lng, mapViewBounds._sw.lat],
          [mapViewBounds._sw.lng, mapViewBounds._ne.lat],
          [mapViewBounds._ne.lng, mapViewBounds._ne.lat],
        ],
      ],
    };

    try {
      const response = await axios.post(hostURL + "api/field", { data });
      const fieldBoundaryFeatures = response.data.field.rows.map((feature) => {
        const geometry = JSON.parse(feature.geometry);
        return {
          type: "Feature",
          geometry: geometry,
          id: feature.gid,
          properties: { gid: feature.gid },
        };
      });
      if (fieldBoundaryFeatures.length === 0) {
        setShowFieldAlert(true);
      } else {
        const fieldBoundaryData = {
          type: "FeatureCollection",
          features: fieldBoundaryFeatures,
        };
        setFieldBoundary(fieldBoundaryData);
      }
    } catch (error) {
      console.log(error.response.data);
    }
  };

  const editField = () => {
    setEditingMode(true);
  };

  const forceFiltersFromCountyRate = async (row) => {
    if (!row) return;

    const nextState = row.state;
    const nextCounty = row.county;
    const nextFips = String(row.fips).padStart(5, "0");

    // If state is changing, load its counties so the UI list stays in sync
    if (selectedState !== nextState) {
      dispatch(setSelectedState(nextState));
      dispatch(fetchGeoCountiesByState(nextState));
    }

    dispatch(setSelectedCounty(nextCounty));
    dispatch(setSelectedFips(nextFips));

    // make the map zoom to the new county
    setForceZoomToCounty(true);

    dispatch(fetchAllPracticeDataByCounty(nextFips));
  };

  const submitField = async () => {
    if (selectedField) {
      const data = selectedField.geometry;

      try {
        setLoading(true);
        const response = await axios.post(hostURL + "api/field/details", {
          data,
        });
        const row = response?.data?.countyRate?.rows?.[0];
        await forceFiltersFromCountyRate(row); // <— force state/county/fips
        updateFieldDetails(response.data);
        secureLocalStorage.setItem("field-details", response.data);
        setDrawingMode(false);
        navigate("/FieldReport");
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.log(error.response.data);
      }
    }
  };

  const submitDraw = async () => {
    if (featureList.length === 0) {
      const msg =
        "Please draw at least one field boundary. \n\nTo complete drawing field boundary, click the first or last vertex you placed.";
      setPolygonAlertMessage(msg);
      setShowPolygonAlert(true);
      setDrawerOpen(true);
      notifyAndBounce(msg);
      return;
    }

    const newList = featureList;
    const data = {
      type: "MultiPolygon",
      coordinates: newList.map((feature) => feature.geometry.coordinates),
    };

    try {
      setLoading(true);
      setShowPolygonAlert(false);
      if (isTutorialActive && currentStep === 54) {
        dispatch(setIsLoading(true));
      }
      const response = await axios.post(hostURL + "api/field/details", {
        data,
      });

      const serverFips = response.data?.countyRate?.rows?.[0]?.fips;
      console.log("SERVER FIELD FIPS: ", serverFips);
      console.log("SELECTED FIPS: ", selectedFips);
      const msg =
        "The polygon you drew is not in one of the available states. Please try a different location or choose another option to import your field..";

      if (!serverFips) {
        setPolygonAlertMessage(msg);
        setShowPolygonAlert(true);
        setDrawerOpen(true);
        setLoading(false);
        notifyAndBounce(msg);
        return;
      }

      if (String(serverFips) !== String(selectedFips)) {
        const msg = `The field you drew is outside of your selected county. Please delete and re-draw a field within your selected county (${selectedCounty}, ${selectedState}) or update your county selection.`;

        setPolygonAlertMessage(msg);
        setShowPolygonAlert(true);
        setDrawerOpen(true);
        setLoading(false);
        notifyAndBounce(msg);
        return;
      }

      updateFieldDetails(response.data);
      secureLocalStorage.setItem("field-details", response.data);
      setDrawingMode(false);
      secureLocalStorage.setItem("IsPastSearch", true);
      if (isTutorialActive && currentStep === 54) {
        handleNextStep();
      }
      navigate("/FieldReport");
      setLoading(false);
    } catch (error) {
      const msg =
        "Something went wrong while checking your field. Please try again later.";
      setPolygonAlertMessage(msg);
      setShowPolygonAlert(true);
      setDrawerOpen(true);
      setLoading(false);
      notifyAndBounce(msg);
      console.log(error.response?.data || error);
    }
  };

  const submitShapefile = async (geometry) => {
    const data = geometry;

    try {
      setLoading(true);
      setShowShapefileAlert(false);
      const response = await axios.post(hostURL + "api/field/details", {
        data,
      });
      const row = response?.data?.countyRate?.rows?.[0];
      await forceFiltersFromCountyRate(row); // <— force state/county/fips
      updateFieldDetails(response.data);
      secureLocalStorage.setItem("field-details", response.data);
      setDrawingMode(false);
      navigate("/FieldReport");
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setShowShapefileAlert(true);
      setDrawerOpen(true);
      console.log(error.response.data);
    }
  };

  const onFileDrop = async (file) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const result = await shp(reader.result);
      if (result) {
        // Features are stored as [0:{}, 1:{}, 2:{}, ...]
        for (var num in result.features) {
          var featureGeometry = result.features[num].geometry;
          // Add geometry type as a parameter to cater to both Polygon and MultiPolygon
          submitShapefile(featureGeometry);
        }
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const dummyRequest = ({ file, onSuccess }) => {
    onFileDrop(file);
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const uploadProps = {
    name: "file",
    accept: ".zip",
    multiple: false,
  };

  useEffect(() => {
    dispatch(fetchGeoStates());
  }, [dispatch]);

  useEffect(() => {
    if (pinCenter) {
      loadPinnedField();
    }
  }, [pinCenter]);

  useEffect(() => {
    if (selectedState && selectedCounty && selectedFips) {
      if (!isMobile) setStep(1);
    }
  }, [selectedState, selectedCounty, selectedFips]);

  return (
    <div className="sidebar">
      <Steps current={step} size="small" />
      {step === 0 && (
        <>
          {userLocation.state ? (
            <h3>
              Your current location is
              <br />
              <b style={{ color: "maroon" }}>{userLocation.county} County, </b>
              <b style={{ color: "maroon" }}>{userLocation.state}</b>
            </h3>
          ) : practicePreference ? (
            <h3>
              You have previously selected
              <br />
              <b style={{ color: "maroon" }}>
                {practicePreference.county} County,{" "}
              </b>
              <b style={{ color: "maroon" }}>{practicePreference.state}</b>
            </h3>
          ) : (
            <h3>Let's find your field area</h3>
          )}
          <motion.div
            className="sidebar-section-1"
            variants={variants}
            whileHover={hoverEffects}
          >
            <p>
              <b>
                Please select the state and county of your field below or click
                on the map:
              </b>
            </p>
            <Select
              className="sidebar-select"
              showSearch
              defaultValue={selectedState}
              placeholder="Select State"
              onChange={onStateChange}
              options={stateList}
              value={selectedState}
            />
            <Select
              className="sidebar-select"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              placeholder="Select County"
              defaultValue={selectedCounty}
              onChange={onCountyChange}
              options={countyList}
              value={selectedCounty ?? null}
            />
          </motion.div>
          <div className="sidebar-section-break">
            <p>
              <b>OR</b>
            </p>
          </div>
          <motion.div
            className="sidebar-section-2"
            variants={variants}
            whileHover={hoverEffects}
          >
            <p>
              <b>
                Please use your current location or search by address of your
                field:
              </b>
            </p>
            <Button className="rectangular-button" onClick={getUserLocation}>
              <MdOutlineMyLocation className="button-icon" />
              <b> Use Current Location</b>
            </Button>
            <AutoComplete
              className="address-input"
              value={addressValue}
              options={addressOptions}
              onChange={onAddressChange}
              placeholder="Search the address here"
              allowClear={true}
            />
            <Button className="submit-button" onClick={onAddressSubmit}>
              <FaSearchLocation className="button-icon" />
              <b> Locate</b>
            </Button>
          </motion.div>
          <div className="button-container">
            <Button className="back-button" onClick={backToPracticeList}>
              <GrLinkPrevious />
              <b> To Explore CRP Practices</b>
            </Button>
            {!isMobile && selectedCounty && (
              <Button className="next-button" onClick={onNextClick}>
                <GrLinkNext />
                <b> Next</b>
              </Button>
            )}
            {isMobile &&
              selectedState &&
              selectedCounty &&
              selectedFips &&
              step === 0 && (
                <div className="mobile-continue next-button">
                  <Button type="primary" block onClick={onNextClick}>
                    {" "}
                    Continue
                  </Button>
                </div>
              )}
          </div>
        </>
      )}

      {(step === 1 || (isTutorialActive && currentStep <= 35)) && (
        <>
          {!inputMode && (
            <>
              <h4>
                Please select one of the options below to outline, import or
                pick your field boundary:
              </h4>
              <div className="sidebar-btn-container">
                {!isMobile && (
                  <Button
                    disabled={
                      isTutorialActive &&
                      (currentStep === 29 || currentStep === 28)
                    }
                    className="square-button polygon-btn"
                    onClick={() => {
                      setInputMode("draw");
                      setDrawingMode(true);
                      setShowNonGeoCheck(true);
                      if (isTutorialActive && currentStep === 35) {
                        dispatch(nextStep());
                      }
                    }}
                  >
                    <LiaDrawPolygonSolid size={"40px"} />

                    <b>Draw Your</b>

                    <b>Field Boundary</b>
                  </Button>
                )}
                {isMobile && (
                  <Button
                    className="square-button boundary-btn"
                    onClick={() => {
                      setInputMode("field");
                      loadFieldBoundary();
                      setShowNonGeoCheck(true);
                      if (setDrawerOpen) setDrawerOpen(false);
                    }}
                  >
                    <MdOutlineSatellite size={"40px"} />
                    <b>Pick From Fields</b>
                  </Button>
                )}
                {!isMobile && (
                  <Button
                    className="square-button shp-button"
                    disabled={
                      isTutorialActive &&
                      (currentStep === 32 || currentStep === 28)
                    }
                    onClick={() => {
                      setInputMode("upload");
                      setShowNonGeoCheck(true);
                    }}
                  >
                    <MdOutlineUploadFile size={"40px"} />
                    <b>Upload Your</b>
                    <b>Field Boundary</b>
                  </Button>
                )}
                <Button
                  className="square-button pin-btn"
                  disabled={
                    isTutorialActive &&
                    (currentStep === 30 || currentStep === 28)
                  }
                  onClick={() => {
                    setInputMode("pin");
                    setPinningMode(true);
                    setShowNonGeoCheck(true);
                    if (setDrawerOpen) setDrawerOpen(false);
                  }}
                >
                  <MdPinDrop size={"40px"} />
                  <b>Drop Your Pin</b>
                </Button>
                {!isMobile && (
                  <Button
                    className="square-button boundary-btn"
                    disabled={
                      isTutorialActive &&
                      (currentStep === 31 || currentStep === 28)
                    }
                    onClick={() => {
                      setInputMode("field");
                      loadFieldBoundary();
                      setShowNonGeoCheck(true);
                      if (setDrawerOpen) setDrawerOpen(false);
                    }}
                  >
                    <MdOutlineSatellite size={"40px"} />
                    <b>Pick From Fields</b>
                  </Button>
                )}
              </div>
              <div className="button-container">
                <Button className="back-button" onClick={backToStep0}>
                  <GrLinkPrevious />
                  <b> Back to County Selection</b>
                </Button>
              </div>
            </>
          )}

          {inputMode === "draw" && (
            <div className="draw-instruction">
              <p>
                <b>
                  Please draw the boundary of your field on the map and click
                  Submit once it's ready.{" "}
                </b>
              </p>
              {!isMobile && (
                <div className="instruction-container">
                  <Button
                    className="instruction-video-button"
                    type="primary"
                    onClick={() => {
                      setShowDrawPolygonVideo(true);
                    }}
                  >
                    <VideoCameraOutlined />
                    Watch the tutorial video
                  </Button>
                  <p className="instruction-paragraph">
                    <u>
                      <b>Instructions</b>
                    </u>
                    : Click on the map to begin drawing your field boundary,
                    then click as you draw each field corner to complete your
                    field shape. Once your field boundary is finished, click the{" "}
                    <i>polygon</i> icon again to make changes. Click the{" "}
                    <i>delete</i> icon to delete your field boundary. Press the
                    ESC button to cancel the drawing process.
                  </p>
                </div>
              )}
              <div>
                <b>
                  For best results, please draw the area of your field you are
                  interested in enrolling in CRP.
                </b>
              </div>
              <Modal
                open={showDrawPolygonVideo}
                onCancel={() => {
                  setShowDrawPolygonVideo(false);
                }}
                onOk={() => {
                  setShowDrawPolygonVideo(false);
                }}
              >
                <video
                  className="instruction-video"
                  width="400px"
                  height="225px"
                  controls
                >
                  <source src={drawPolygonVideo} type="video/mp4"></source>
                </video>
              </Modal>
              {showPolygonAlert && (
                <Alert
                  message="Error"
                  description={
                    polygonAlertMessage || "An unexpected error occurred."
                  }
                  type="error"
                  showIcon
                  closable
                />
              )}

              <div className="button-container">
                <Button className="back-button" onClick={backToStep1}>
                  <GrLinkPrevious />
                  <b> Back</b>
                </Button>
                <Button className="submit-button" onClick={submitDraw}>
                  <b>Submit </b>
                  <GrLinkNext />
                </Button>
              </div>
            </div>
          )}

          {inputMode === "upload" && (
            <div className="upload-instruction">
              <p>
                <b>
                  Please drag and drop the zipped shapefile for your field
                  boundary here.
                </b>
              </p>
              {!isMobile && (
                <div className="instruction-container">
                  <Button
                    className="instruction-video-button"
                    type="primary"
                    onClick={() => {
                      setShowUploadShapefileVideo(true);
                    }}
                  >
                    <VideoCameraOutlined />
                    Watch the tutorial video
                  </Button>
                  <p className="instruction-paragraph">
                    <u>
                      <b>Instructions</b>
                    </u>
                    : Collect the shapefiles of your field in the same folder
                    (must include .dbf, .prj, .shp and .shx files) and zip them
                    into a .zip file. Upload or drag the .zip file into this
                    drop area.
                  </p>
                </div>
              )}
              <Modal
                open={showUploadShapefileVideo}
                onCancel={() => {
                  setShowUploadShapefileVideo(false);
                }}
                onOk={() => {
                  setShowUploadShapefileVideo(false);
                }}
              >
                <video
                  className="instruction-video"
                  width="400px"
                  height="225px"
                  controls
                >
                  <source src={uploadShapefileVideo} type="video/mp4"></source>
                </video>
              </Modal>
              <Dragger {...uploadProps} customRequest={dummyRequest}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Support for a single zipped shapefile upload. Strictly
                  prohibited from uploading malicious data or other unrelated
                  files.
                </p>
              </Dragger>
              {showShapefileAlert && (
                <Alert
                  message="Error"
                  description="The shapefile you uploaded is not in one of the available states. Please try a different location or choose another option to import your field."
                  type="error"
                  showIcon
                  closable
                />
              )}
              <div className="button-container">
                <Button className="back-button" onClick={backToStep1}>
                  <GrLinkPrevious />
                  <b> Back</b>
                </Button>
              </div>
            </div>
          )}

          {inputMode === "pin" && (
            <div className="pin-instruction">
              {selectedField ? (
                <>
                  <p>
                    {!isMobile ? (
                      <b>
                        You have dropped the pin above one of the pregenerated
                        field boundaries. Please select one of the following
                        options:
                      </b>
                    ) : (
                      <b>
                        You have selected one field from the pregenerated field
                        boundaries. Please know these fields are generated from
                        satelite images and may not be accurate.
                      </b>
                    )}
                  </p>
                  {!isMobile && (
                    <div className="sidebar-btn-container" p>
                      <Button
                        className="square-button"
                        onClick={editField}
                        disabled={editingMode ? true : false}
                      >
                        <MdRebaseEdit size={"40px"} />
                        <b>Edit Selected</b>
                        <b>Field Boundary</b>
                      </Button>
                      <Button className="square-button" onClick={submitField}>
                        <MdOutlineNextPlan size={"40px"} />
                        <b>Proceed With</b>
                        <b>Selected Field</b>
                      </Button>
                    </div>
                  )}
                  <div>
                    {!isMobile ? (
                      <b>
                        For best results, please edit selected field boundary to
                        specify the area of your field you are interested in
                        enrolling in CRP.
                      </b>
                    ) : (
                      <b>
                        For the most accurate field boundaries please use a
                        desktop or tablet (in landscape mode) and use the "Draw
                        Your Field Boundary" option.
                      </b>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p>
                    <b>
                      Please drop a pin on the map to locate a pregenerated
                      field boundary.
                    </b>
                  </p>
                  {!isMobile && (
                    <div className="instruction-container">
                      <Button
                        className="instruction-video-button"
                        type="primary"
                        onClick={() => {
                          setShowDropPinVideo(true);
                        }}
                      >
                        <VideoCameraOutlined />
                        Watch the tutorial video
                      </Button>
                      <p className="instruction-paragraph">
                        <u>
                          <b>Instructions</b>
                        </u>
                        : Click on the map to drop a pin when you see the cursor
                        turns into a cross and wait for the field boundary to
                        load. If you see an error, it means there is no
                        pregenerated field boundary at the exact pin location.
                        Once the field boundary is loaded, choose one of the two
                        options to edit the polygon or proceed with the field
                        once ready.
                      </p>
                    </div>
                  )}
                  <Modal
                    width="1070px"
                    open={showDropPinVideo}
                    onCancel={() => {
                      setShowDropPinVideo(false);
                    }}
                    onOk={() => {
                      setShowDropPinVideo(false);
                    }}
                  >
                    <video
                      className="instruction-video"
                      width="950px"
                      height="360px"
                      controls
                    >
                      <source src={dropPinVideo} type="video/mp4"></source>
                    </video>
                  </Modal>
                  {showPinAlert && (
                    <Alert
                      message="Error"
                      description="The pin you dropped doesn't overlap with any of the pregenerated field boundaries. Please try a different location or choose another option to outline or import your field."
                      type="error"
                      showIcon
                      closable
                    />
                  )}
                </>
              )}
              {!isMobile ? (
                <div className="button-container">
                  <Button className="back-button" onClick={backToStep1}>
                    <GrLinkPrevious />
                    <b> Back</b>
                  </Button>
                </div>
              ) : (
                <div className="button-container">
                  <Button className="back-button" onClick={backToStep1}>
                    <GrLinkPrevious />
                    <b> Back</b>
                  </Button>
                  {isMobile && selectedField && (
                    <Button type="primary" onClick={submitField}>
                      <b>Continue</b>
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {inputMode === "field" && (
            <div className="field-instruction">
              {selectedField ? (
                <>
                  <p>
                    {!isMobile ? (
                      <b>
                        You have selected one field from the pregenerated field
                        boundaries. Please select one of the following options:
                      </b>
                    ) : (
                      <b>
                        You have selected one field from the pregenerated field
                        boundaries. Please note that these fields are generated from
                        satellite images and may not represent your field accurately.
                      </b>
                    )}
                  </p>
                  {!isMobile && (
                    <div className="sidebar-btn-container">
                      <Button
                        className="square-button"
                        onClick={editField}
                        disabled={editingMode ? true : false}
                      >
                        <MdRebaseEdit size={"40px"} />
                        <b>Edit Selected</b>
                        <b>Field Boundary</b>
                      </Button>
                      <Button className="square-button" onClick={submitField}>
                        <MdOutlineNextPlan size={"40px"} />
                        <b>Proceed With</b>
                        <b>Selected Field</b>
                      </Button>
                    </div>
                  )}
                  <div>
                    {!isMobile ? (
                      <b>
                        For best results, please edit selected field boundary to
                        specify the area of your field you are interested in
                        enrolling in CRP.
                      </b>
                    ) : (
                      <b>
                        For the most accurate field boundaries please use a
                        desktop or tablet (in landscape mode) and use the "Draw
                        Your Field Boundary" option.
                      </b>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p>
                    <b>
                      Please pick your field from the pregenerated field
                      boundaries:
                    </b>
                  </p>
                  {!isMobile && (
                    <div className="instruction-container">
                      <Button
                        className="instruction-video-button"
                        type="primary"
                        onClick={() => {
                          setShowPickFieldVideo(true);
                        }}
                      >
                        <VideoCameraOutlined />
                        Watch the tutorial video
                      </Button>
                      <p className="instruction-paragraph">
                        <u>
                          <b>Instructions</b>
                        </u>
                        : Click on the map to select one of the pregenerated
                        fields. If you see an error, it means there is no
                        pregenerated field boundary within the current map view.
                        Once the field boundary is selected, choose one of the
                        two options to edit the polygon or proceed with the
                        field once ready.
                      </p>
                    </div>
                  )}
                  <Modal
                    width="1070px"
                    open={showPickFieldVideo}
                    onCancel={() => {
                      setShowPickFieldVideo(false);
                    }}
                    onOk={() => {
                      setShowPickFieldVideo(false);
                    }}
                  >
                    <video
                      className="instruction-video"
                      width="950px"
                      height="360px"
                      controls
                    >
                      <source src={pickFieldVideo} type="video/mp4"></source>
                    </video>
                  </Modal>
                  {showFieldAlert && (
                    <Alert
                      message="Error"
                      description="There is no pregenerated field boundary under your current map view. Please zoom out and retry or choose another option to outline or import your field."
                      type="error"
                      showIcon
                      closable
                    />
                  )}
                </>
              )}
              <div className="button-container">
                <Button className="back-button" onClick={backToStep1}>
                  <GrLinkPrevious />
                  <b> Back</b>
                </Button>
                {isMobile && selectedField && (
                  <Button type="primary" onClick={submitField}>
                    <b>Continue</b>
                  </Button>
                )}
              </div>
            </div>
          )}
          {showNonGeoCheck && <NonGeoCheck />}
        </>
      )}
    </div>
  );
};

export default Sidebar;
